import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { CanonicalSourceJobLine } from '@/lib/tickets/optiPlaceAnalysisInput';

export type HydrationState = 'loading' | 'ready' | 'error';

export interface CanonicalSourceJobLinesResult {
  /**
   * The canonical `ticket_jobs` rows for the active persisted source.
   *
   * These come straight from the database and carry `ticket_jobs.job_id` as
   * their identity. Nothing here is correlated back to `ticketData.jobs` by
   * name, index, category or catalog id — the DB result IS the identity.
   */
  sourceJobLines: CanonicalSourceJobLine[] | null;
  /** The ticket `sourceJobLines` was read FOR. */
  sourceJobLinesTicketId: string | null;
  /** Hydration is a STATE, not a timeout. */
  hydration: HydrationState;
}

/**
 * 8G.2D.2D.3 §6 — canonical job-line hydration with REQUEST OWNERSHIP.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS A HOOK AND NOT AN EFFECT IN THE MODAL
 * ═══════════════════════════════════════════════════════════════════════════
 * The previous guard lived inline in `OptiPlaceModal` and read:
 *
 *     const requestedTicketId = ticketData.id;      // captured by THIS render
 *     query.then(() => {
 *       if (requestedTicketId !== ticketData.id) return;   // SAME closure
 *       ...
 *     });
 *
 * Both sides of that comparison belong to the same effect closure. When the
 * effect ran for ticket A, `ticketData.id` inside A's callback is A forever —
 * a later render for ticket B creates a NEW closure and does not mutate the
 * old one. So the comparison was `A !== A`, which is never true, and the guard
 * could not reject anything. Addendum 58 claimed out-of-order responses were
 * rejected; that claim was wrong, and this replaces it.
 *
 * ── THE LAW ────────────────────────────────────────────────────────────────
 * ONLY the most recent hydration request for the currently active persisted
 * ticket may mutate `sourceJobLines`, `sourceJobLinesTicketId` or `hydration`.
 * A response belonging to a superseded request is a complete NO-OP.
 *
 * The mechanism is a monotonically increasing generation held in a ref. A ref
 * is shared across renders, so a stale callback reads the CURRENT generation
 * rather than the one its own closure captured — which is precisely the
 * property the old comparison lacked. Every path that ends a request's claim
 * to ownership bumps the generation: a new ticket id, the modal closing, the
 * ticket becoming unpersisted, and unmount.
 *
 * Stale ownership is therefore unrepresentable rather than merely checked: a
 * superseded callback cannot name the current generation, because the only way
 * to obtain it is to be the request that most recently incremented it.
 */
export function useCanonicalSourceJobLines(
  isOpen: boolean,
  ticketId: string | null | undefined,
): CanonicalSourceJobLinesResult {
  const [sourceJobLines, setSourceJobLines] =
    useState<CanonicalSourceJobLine[] | null>(null);
  const [sourceJobLinesTicketId, setSourceJobLinesTicketId] =
    useState<string | null>(null);
  const [hydration, setHydration] = useState<HydrationState>('loading');

  // Lazily created so the module can be imported outside React without
  // touching React internals; `useState` gives us one stable box per mount.
  const [generationRef] = useState(() => ({ current: 0 }));

  useEffect(() => {
    // Any change of identity — including closing — retires every in-flight
    // request. This runs BEFORE the new request is issued, so the request
    // below always owns a generation no earlier response can name.
    const generation = ++generationRef.current;

    if (!isOpen || !ticketId) {
      setSourceJobLines(null);
      setSourceJobLinesTicketId(null);
      // An unpersisted ticket has nothing to hydrate; it must not be treated
      // as forever-loading or its analysis would never run.
      setHydration('ready');
      return;
    }

    // Bind and reset SYNCHRONOUSLY, before any await. If this happened in the
    // callback instead, the window between the id changing and the rows
    // arriving would still expose the previous ticket's lines as `ready`.
    const requestedTicketId = ticketId;
    setSourceJobLines(null);
    setSourceJobLinesTicketId(null);
    setHydration('loading');

    // Read under the user's own authority/RLS like any other read.
    void supabase
      .from('ticket_jobs')
      .select(
        'job_id, job_status, minutes_override, jobs(name, category, submenu, skill_level, bay_requirement, standard_minutes, proficiency_category)',
      )
      .eq('ticket_id', requestedTicketId)
      .then(({ data, error }: { data: any; error: any }) => {
        // ══ REQUEST OWNERSHIP ═════════════════════════════════════════════
        // `generation` is this request's own; `generationRef.current` is
        // shared mutable state read at RESOLUTION time. A superseded request
        // sees a larger current value and returns without touching anything.
        if (generation !== generationRef.current) return;

        if (error) {
          setSourceJobLines(null);
          setSourceJobLinesTicketId(null);
          setHydration('error');
          return;
        }

        setSourceJobLines(
          (data ?? []).map((r: any) => ({
            job_id: r.job_id,
            job_status: r.job_status ?? null,
            minutes_override: r.minutes_override ?? null,
            name: r.jobs?.name ?? '',
            category: r.jobs?.category ?? '',
            submenu: r.jobs?.submenu ?? null,
            skill_level: r.jobs?.skill_level ?? null,
            bay_requirement: r.jobs?.bay_requirement ?? null,
            standard_minutes: r.jobs?.standard_minutes ?? null,
            proficiency_category: r.jobs?.proficiency_category ?? null,
          })),
        );
        setSourceJobLinesTicketId(requestedTicketId);
        setHydration('ready');
      });

    // MEASURED: this is NOT load-bearing, and a controlled breakage proved it.
    // Removing it leaves every test green, because the `++` at the top of the
    // effect already retires in-flight reads on EVERY dep change — close, id
    // change, and the persisted→unpersisted transition all re-run the effect.
    // What remains is unmount, where React 18 makes a setState on an unmounted
    // component a silent no-op regardless. It is kept as defense-in-depth
    // against a future edit that moves or removes the leading increment, not
    // because it currently changes behaviour.
    return () => {
      generationRef.current++;
    };
  }, [isOpen, ticketId, generationRef]);

  return { sourceJobLines, sourceJobLinesTicketId, hydration };
}
